import { Image, Upload, UploadFile, UploadProps } from 'antd';
import { ReactNode, useEffect, useState } from 'react';
import { CloudUploadOutlined } from '@ant-design/icons';
import magnifyIcon from '../../../../../assets/svg/magnify.svg';
import closeIcon from '../../../../../assets/svg/close.svg';
import loadingIcon from '../../../../../assets/svg/loading-icon.svg';
import styles from './index.module.scss';
import getFormatMessage from '../../../../../locales/get-format-message';
import { messageError } from '../../../../../components/message';
import { ICompanyUploadValue } from '../../../../../model';
import { getCompanyFileUrl, putCompanyObject } from '../../../lib/company-cos';
import { SpriteSvg, SpriteSvgType } from '../../../../../components/sprite-svg';
import classNames from 'classnames';

interface IProps {
  onChange?: (fileKey: ICompanyUploadValue | undefined) => void;
  value?: ICompanyUploadValue;
  disabled?: boolean;
}

export const UploadCert = (props: IProps) => {
  const { disabled, value, onChange } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string>();
  const [showPreview, setShowPreview] = useState<boolean>(false);

  useEffect(() => {
    init();
  }, [value]);

  const init = async () => {
    if (!value) {
      return;
    }
    setPreviewImage(value.url);
  };

  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || (file.preview as string));
  };

  const beforeUpload: UploadProps['beforeUpload'] = async (uploadFile) => {
    const file = uploadFile as File;
    if (file.size > 4 * 1024 * 1024) {
      messageError(getFormatMessage('company_upload_err'));
      return false;
    }
    setLoading(true);
    setPreviewImage(undefined);

    const fileName = `${new Date().getTime()}_${file.name}`;

    await putCompanyObject(fileName, file);

    const url = await getCompanyFileUrl(fileName);

    setPreviewImage(url);
    onChange?.({ fileName, url });

    setLoading(false);
    return false;
  };

  const handleDeleteImg = (ev: React.MouseEvent<SVGElement>) => {
    setPreviewImage('');
    ev.stopPropagation();
    onChange?.(undefined);
  };

  const renderContent = () => {
    const getPreview = (extra?: ReactNode) => {
      return (
        <div className={styles.certContent}>
          <div
            className={styles.certPreview}
            style={{
              // @ts-ignore
              '--load-fail-text': `"${getFormatMessage('加载失败')}"`,
            }}
            onClick={(ev) => {
              ev.stopPropagation();
            }}
          >
            {!!previewImage && (
              <>
                <div className={styles.magnifyIcon}>
                  <img
                    src={magnifyIcon}
                    onClick={() => {
                      setShowPreview(true);
                    }}
                  />
                </div>
                <Image
                  src={previewImage}
                  width={100}
                  preview={{
                    visible: showPreview,
                    icons: {
                      close: (
                        <div>
                          <div className={styles.previewCloseIcon}>
                            <img src={closeIcon} />
                          </div>
                        </div>
                      ),
                    },
                    getContainer: () =>
                      document.querySelector(
                        `.${styles.certPreview}`
                      ) as HTMLElement,
                    onVisibleChange: (value) => {
                      setShowPreview(value);
                    },
                  }}
                />
              </>
            )}
          </div>
          {extra}
        </div>
      );
    };
    if (disabled) {
      return getPreview();
    }
    return (
      <>
        {!!previewImage && (
          <>
            {getPreview(
              <div className={styles.uploadTool}>
                <span className={styles.space}></span>
                <SpriteSvg
                  className={styles.reUploadIcon}
                  type={SpriteSvgType.Refresh}
                />
                <SpriteSvg
                  className={styles.delIcon}
                  type={SpriteSvgType.Delete}
                  onClick={handleDeleteImg}
                />
              </div>
            )}
          </>
        )}
        {loading && (
          <div className={styles.certLoadingBox}>
            <img src={loadingIcon} className={styles.loadingIcon} />
            <span>{getFormatMessage('company_uploading_cert')}...</span>
          </div>
        )}
        {!previewImage && !loading && (
          <div className={styles.uploadIcon}>
            <CloudUploadOutlined />
            <span>{getFormatMessage('company_upload_warn')}</span>
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={classNames(styles.certUpload, {
        [styles.notUpload]: !previewImage && !loading,
      })}
    >
      <Upload
        onPreview={handlePreview}
        beforeUpload={beforeUpload}
        fileList={[]}
        accept=".jpg,.jpeg,.png,.JPG,.JPEG,.PNG"
      >
        {renderContent()}
      </Upload>
    </div>
  );
};
